// ============================
// BUNDLE MANAGER - Integrated with Cart System
// Works with CartStateManager, CartEvents, and CartDOM
// ============================

// Define standard cart events
const cartEvents = ['cart:updated', 'cart:refresh', 'cart:change', 'cart:rendered'];

class BundleManager {
  constructor() {
    this.bundleContainer = document.querySelector('.product-bundle');
    if (!this.bundleContainer) return;
    
    this.blockId = this.bundleContainer.closest('[data-block-id]')?.dataset.blockId || '';
    this.modal = document.querySelector('#BundleModal');
    if (!this.modal) {
      console.error('Modal with ID #BundleModal not found.');
      return;
    }
    
    this.selectedProducts = [null, null, null]; // 3 slots for products
    this.bundleProductsData = null;
    this.currentSlot = null;
    this.discountPercentage = parseInt(this.bundleContainer.querySelector('.product-bundle__discount')?.textContent.match(/\d+/)?.[0] || 0);
    
    // Cart system integration
    this.cartSubscription = null;
    this.isProcessingBundleCheck = false;
    
    // Bundle product identification
    this.coffretProductId = null;
    this.coffretProductHandle = 'coffret-sur-mesure-3x15ml';
    
    // Find the original add to cart button and form
    this.originalAddToCartButton = document.querySelector('[name="add"]') || document.querySelector('.product-form__cart-submit');
    this.productForm = document.querySelector('product-form form') || document.querySelector('form[action*="/cart/add"]');
    
    this.init();
  }

  async init() {
    await this.loadBundleProducts();
    this.setupModal();
    this.setupOriginalButtonIntegration();
    
    // Integrate with cart system
    this.setupCartIntegration();

  }

  // ============================
  // CART SYSTEM INTEGRATION
  // ============================
  
  setupCartIntegration() {
    // Subscribe to cart state changes via CartStateManager
    if (window.CartStateManager?.subscribe) {
      this.cartSubscription = window.CartStateManager.subscribe(
        this.handleCartStateChange.bind(this),
        {
          id: 'bundle-manager',
          priority: 10, // High priority to handle before other modules
          debounce: false // Immediate response needed
        }
      );
    }
    
    // Listen for manual cart events as backup
    document.addEventListener('cart:updated', this.handleCartEvent.bind(this));
    document.addEventListener('cart:refresh', this.handleCartEvent.bind(this));
    
    // Add checkout button interceptor
    this.setupCheckoutInterceptor();
    
    // Get initial cart state to identify main product ID
    this.initializeMainProductId();
  }

  setupCheckoutInterceptor() {
    // Watch for dynamic checkout button additions
    const checkoutButtonObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          this.attachCheckoutValidation();
        }
      });
    });

    // Observe cart drawer and form for dynamic updates
    const cartDrawer = document.querySelector('#CartDrawer, cart-drawer, .cart-drawer');
    const cartForm = document.querySelector('form[action="/cart"]');
    
    if (cartDrawer) {
      checkoutButtonObserver.observe(cartDrawer, { childList: true, subtree: true });
    }
    if (cartForm) {
      checkoutButtonObserver.observe(cartForm, { childList: true, subtree: true });
    }

    // Initial attachment
    this.attachCheckoutValidation();
  }

  attachCheckoutValidation() {
    const checkoutButtons = document.querySelectorAll(
      '[name="checkout"], [href="/checkout"], #checkout, .checkout-button, .cart__checkout-button'
    );

    checkoutButtons.forEach(button => {
      if (!button.hasAttribute('data-bundle-validated')) {
        button.setAttribute('data-bundle-validated', 'true');
        
        const clickHandler = async (event) => {
          event.preventDefault();
          event.stopPropagation();

          const isValid = await this.validateCartForCheckout();
          
          if (isValid) {
            // Remove the event listener to allow the click through
            button.removeEventListener('click', clickHandler);
            
            // Proceed with checkout
            if (button.tagName === 'A') {
              window.location.href = button.href;
            } else {
              button.form?.submit() || (window.location.href = '/checkout');
            }
            
            // Re-attach the event listener for future clicks
            setTimeout(() => {
              button.addEventListener('click', clickHandler);
            }, 100);
          }
        };
        
        button.addEventListener('click', clickHandler);
      }
    });
  }

  async validateCartForCheckout() {
    try {
      // Force a fresh cart fetch
      const cart = await window.CartStateManager?.getCart(true) || await this.getCartDirect();
      
      if (!cart?.items?.length) return true;

      // Find bundle items and main coffret
      const bundleItems = cart.items.filter(item => 
        item.properties && (
          item.properties['Part of'] === 'Coffret Sur Mesure 3x15ml' ||
          item.properties['Coffret-Bundle'] === 'true'
        )
      );

      const mainCoffret = cart.items.find(item => 
        item.product_id === this.coffretProductId || 
        item.handle === this.coffretProductHandle ||
        item.product_handle === this.coffretProductHandle ||
        (item.properties && item.properties.Bundle === 'Coffret Sur Mesure 3x15ml')
      );

      // If there are bundle items but no main coffret
      if (bundleItems.length > 0 && !mainCoffret) {
        // Block checkout and clean up
        const message = Shopify.locale === 'fr' 
          ? 'Impossible de procéder au paiement. Nettoyage du panier en cours...'
          : 'Cannot proceed to checkout. Cleaning cart...';
        alert(message);

        // Clean up bundle items
        await this.cleanupBundleFragrances(bundleItems);
        
        // Refresh cart UI
        await this.refreshCartState();
        return false;
      }

      return true;

    } catch (error) {
      console.error('Error validating cart for checkout:', error);
      
      // SECURITY: Don't allow checkout on validation errors
      // Log the error but show a user-friendly message
      const message = Shopify.locale === 'fr' 
        ? 'Erreur lors de la validation du panier. Veuillez actualiser la page et réessayer.'
        : 'Error validating cart. Please refresh the page and try again.';
      alert(message);
      
      return false; // Block checkout on validation error for security
    }
  }

  async getCartDirect() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Failed to fetch cart');
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart directly:', error);
      return null;
    }
  }
  
  async initializeMainProductId() {
    try {
      if (window.CartStateManager?.getCart) {
        const cart = await window.CartStateManager.getCart();
        this.identifyMainProductId(cart);
      }
    } catch (error) {
      console.error('Error initializing main product ID:', error);
    }
  }
  
  identifyMainProductId(cart) {
    if (!cart?.items) return;
    
    const mainProduct = cart.items.find(item => 
      item.handle === this.coffretProductHandle ||
      item.product_handle === this.coffretProductHandle ||
      (item.properties && item.properties.Bundle === 'Coffret Sur Mesure 3x15ml')
    );
    
    if (mainProduct) {
      this.coffretProductId = mainProduct.product_id;

    }
  }
  
  handleCartStateChange(newCart, previousCart) {
    if (this.isProcessingBundleCheck) return;
    
    this.identifyMainProductId(newCart);
    this.checkForBundleCleanup(newCart, previousCart);
  }
  
  handleCartEvent(event) {
    if (this.isProcessingBundleCheck) return;
    
    // Small delay to allow cart operations to complete
    setTimeout(() => {
      this.checkForBundleCleanupFromEvent();
    }, 100);
  }
  
  async checkForBundleCleanupFromEvent() {
    try {
      if (window.CartStateManager?.getCart) {
        const cart = await window.CartStateManager.getCart(true); // Force refresh
        this.checkForBundleCleanup(cart);
      }
    } catch (error) {
      console.error('Error checking for bundle cleanup:', error);
    }
  }
  
  // ============================
  // BUNDLE CLEANUP LOGIC
  // ============================
  
  checkForBundleCleanup(newCart, previousCart = null) {
    if (!newCart?.items) return;
    
    // Check if main bundle product exists
    const mainProduct = newCart.items.find(item => 
      item.product_id === this.coffretProductId || 
      item.handle === this.coffretProductHandle ||
      item.product_handle === this.coffretProductHandle ||
      (item.properties && item.properties.Bundle === 'Coffret Sur Mesure 3x15ml')
    );
    
    // Find bundle fragrance items
    const bundleFragrances = newCart.items.filter(item => 
      item.properties && (
        item.properties['Part of'] === 'Coffret Sur Mesure 3x15ml' ||
        item.properties['Coffret-Bundle'] === 'true'
      )
    );
    
    // If no main product but there are bundle items, clean them up immediately
    if (!mainProduct && bundleFragrances.length > 0) {
      // Set a flag to prevent race conditions during cleanup
      if (this.isProcessingBundleCheck) return;
      
      // Implement immediate cleanup with retry mechanism
      this.cleanupBundleFragrances(bundleFragrances);
      
      // Set a backup timeout to double-check after a delay
      setTimeout(() => {
        this.checkForBundleCleanupFromEvent();
      }, 2000);
    }
  }
  
  async cleanupBundleFragrances(bundleFragrances) {
    if (!bundleFragrances || bundleFragrances.length === 0) return;
    
    this.isProcessingBundleCheck = true;
    
    try {
      // Block checkout buttons during cleanup
      this.disableCheckoutButtons();
      
      // Use CartStateManager for removal if available
      const removalPromises = bundleFragrances.map(item => {
        if (window.CartStateManager?.updateCartItem) {
          return window.CartStateManager.updateCartItem({
            id: item.key,
            quantity: 0
          });
        } else {
          // Fallback to direct API call
          return this.removeCartItemDirect(item);
        }
      });
      
      await Promise.all(removalPromises);
      
      // Verify cleanup was successful
      const currentCart = await window.CartStateManager?.getCart(true) || await this.getCartDirect();
      const remainingBundleItems = currentCart?.items?.filter(item => 
        item.properties && (
          item.properties['Part of'] === 'Coffret Sur Mesure 3x15ml' ||
          item.properties['Coffret-Bundle'] === 'true'
        )
      ) || [];
      
      // If items remain, retry cleanup
      if (remainingBundleItems.length > 0) {
        console.warn('Bundle cleanup incomplete, retrying...');
        await this.cleanupBundleFragrances(remainingBundleItems);
      }
      
      // Refresh cart state after cleanup
      await this.refreshCartState();
      
    } catch (error) {
      console.error('Error during bundle cleanup:', error);
    } finally {
      this.isProcessingBundleCheck = false;
      this.enableCheckoutButtons();
    }
  }

  disableCheckoutButtons() {
    const checkoutButtons = document.querySelectorAll(
      '[name="checkout"], [href="/checkout"], #checkout, .checkout-button, .cart__checkout-button'
    );
    
    checkoutButtons.forEach(button => {
      button.disabled = true;
      button.style.opacity = '0.5';
      button.style.pointerEvents = 'none';
    });
  }

  enableCheckoutButtons() {
    const checkoutButtons = document.querySelectorAll(
      '[name="checkout"], [href="/checkout"], #checkout, .checkout-button, .cart__checkout-button'
    );
    
    checkoutButtons.forEach(button => {
      button.disabled = false;
      button.style.opacity = '';
      button.style.pointerEvents = '';
    });
  }

  async removeCartItemDirect(item) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: item.key,
          quantity: 0
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to remove item: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error removing cart item ${item.title}:`, error);
      throw error;
    }
  }

  // ============================
  // ORIGINAL BUTTON INTEGRATION
  // ============================
  
  setupOriginalButtonIntegration() {
    if (!this.originalAddToCartButton || !this.productForm) {
      console.warn('Original add to cart button or form not found');
      return;
    }

    // Only intercept when the bundle UI is present on the page. We rely on the bundle
    // container (the constructor already bails out if `.product-bundle` is missing) rather
    // than a URL handle match, so the gating still works on localized/translated URLs.
    if (!this.bundleContainer) {
      return;
    }

    // Store original button text
    if (!this.originalAddToCartButton.hasAttribute('data-original-text')) {
      this.originalAddToCartButton.setAttribute('data-original-text', this.originalAddToCartButton.textContent);
    }

    // Intercept the original button click
    this.originalAddToCartButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      
      const selectedCount = this.selectedProducts.filter(p => p !== null).length;
      
      if (selectedCount < 3) {
        // Not enough fragrances selected, show message
        this.showBundleRequiredMessage(selectedCount);
      } else {
        // Complete bundle, add everything
        this.addCompleteBundleToCart();
      }
    });

    // Also intercept form submission as backup (e.g. Enter key). This listener is only
    // attached when the bundle UI is present, so no URL check is needed here.
    this.productForm.addEventListener('submit', (event) => {
      event.preventDefault();
      const selectedCount = this.selectedProducts.filter(p => p !== null).length;

      if (selectedCount < 3) {
        this.showBundleRequiredMessage(selectedCount);
      } else {
        this.addCompleteBundleToCart();
      }
    });

    // Set the initial gated state (0 selected → disabled + "Sélectionnez 3 parfums de plus"),
    // keeping the JS state in sync with the disabled attribute rendered in HTML.
    this.showAddToCartButton();
  }

  showBundleRequiredMessage(selectedCount) {
    if (!this.originalAddToCartButton) return;

    const remaining = 3 - selectedCount;
    const message = Shopify.locale === 'fr' 
      ? `Sélectionnez ${remaining} parfum${remaining > 1 ? 's' : ''} de plus`
      : `Select ${remaining} more fragrance${remaining > 1 ? 's' : ''}`;
    
    this.originalAddToCartButton.textContent = message;
    this.originalAddToCartButton.disabled = true;

    setTimeout(() => {
      // Re-sync from the real selection state instead of blindly re-enabling: the button
      // must stay disabled while the bundle is incomplete (fail-safe gating).
      this.showAddToCartButton();
    }, 2000);
  }

  async addCompleteBundleToCart() {
    if (!this.originalAddToCartButton) return;

    try {
      // Show loading state on original button
      this.originalAddToCartButton.textContent = Shopify.locale === 'fr' ? 'Ajout en cours...' : 'Adding to cart...';
      this.originalAddToCartButton.disabled = true;

      // Show bundle loading overlay
      showBundleLoadingOverlay();

      // Check if cart was empty BEFORE adding
      let cartWasEmpty = false;
      if (window.CartStateManager) {
        const currentCart = await window.CartStateManager.getCart(false);
        cartWasEmpty = !currentCart || currentCart.item_count === 0;
        console.log('[Bundle] Cart was empty:', cartWasEmpty);
      }

      // Prepare all items (main product + fragrances) for bulk add
      const allItems = await this.prepareAllBundleItems();
      
      // Add all items at once using bulk add
      const result = await this.addItemsBulk(allItems);
      console.log('[Bundle] Items added to cart:', result);

      // Wait a moment for Shopify to process
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Clear selections
      this.clearSelectedProducts();
      
      // Force refresh cart state
      let updatedCart;
      if (window.CartStateManager) {
        updatedCart = await window.CartStateManager.getCart(true);
        console.log('[Bundle] Cart state refreshed, items:', updatedCart.item_count);
      }
      
      // Success - resync button from selection state. Selections were just cleared, so this
      // leaves the button disabled again (fail-safe) instead of re-enabling it for a 2nd add.
      this.showAddToCartButton();

      // Hide bundle loading overlay
      hideBundleLoadingOverlay();
      
      // Check if iOS
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      
      // If cart was empty, reload to refresh cart state
      if (cartWasEmpty) {
        if (isIOS) {
          // On iOS: reload then show notification instead of auto-opening
          console.log('[Bundle] iOS empty cart: reloading then showing notification');
          sessionStorage.setItem('showBundleNotificationAfterRefresh', 'true');
        } else {
          // Other platforms: reload then auto-open drawer
          console.log('[Bundle] Cart was empty, reloading then opening drawer...');
          sessionStorage.setItem('openCartAfterRefresh', 'true');
        }
        window.location.reload();
        return;
      }
      
      // For non-empty cart, use DOM refresh
      await this.openCartDrawer();
      await new Promise(resolve => setTimeout(resolve, 600));
      
      if (window.CartDOM?.refreshCartDrawerFull) {
        await window.CartDOM.refreshCartDrawerFull('bundle-added', false);
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { source: 'bundle' }}));
        
        // iOS specific: Force drawer height recalculation after DOM refresh
        await new Promise(resolve => setTimeout(resolve, 100));
        this.fixIOSDrawerHeight();
      } else {
        console.warn('[Bundle] CartDOM manager not available, fallback to reload');
        window.location.reload();
      }
      
    } catch (error) {
      console.error('Error adding bundle to cart:', error);
      
      // Hide bundle loading overlay
      hideBundleLoadingOverlay();

      // Restore button from selection state. The 3 fragrances are still selected here (the
      // add failed before clearing), so this re-enables the button for a retry.
      this.showAddToCartButton();

      alert(Shopify.locale === 'fr' ? 'Erreur lors de l\'ajout au panier. Veuillez réessayer.' : 'Error adding to cart. Please try again.');
    }
  }

  async prepareAllBundleItems() {
    const allItems = [];

    // 1. Add main coffret product
    const variantInput = this.productForm.querySelector('[name="id"]');
    if (!variantInput) {
      console.error('[Bundle] ❌ Variant input not found in form');
      throw new Error('Variant ID not found');
    }

    const variantId = variantInput.value;
    const quantity = this.productForm.querySelector('[name="quantity"]')?.value || 1;

    console.log('[Bundle] Main product variant ID:', variantId, 'quantity:', quantity);

    const mainProductData = {
      id: parseInt(variantId), // Ensure it's a number
      quantity: parseInt(quantity),
      properties: {
        'Bundle': 'Coffret Sur Mesure 3x15ml'
      }
    };
    
    allItems.push(mainProductData);
    console.log('[Bundle] Main product prepared:', mainProductData);

    // 2. Add selected fragrance products
    const selectedProducts = this.selectedProducts.filter(p => p !== null);
    console.log('[Bundle] Selected fragrances:', selectedProducts.length);
    
    if (selectedProducts.length > 0) {
      const verifiedProducts = await Promise.all(
        selectedProducts.map(product => this.verifyAndGetValidVariant(product))
      );

      const fragranceItems = verifiedProducts.map((product, index) => {
        const item = {
          id: parseInt(product.validVariantId), // Ensure it's a number
          quantity: 1,
          properties: { 
            'Part of': 'Coffret Sur Mesure 3x15ml',
            'Coffret-Bundle': 'true',
            '_bundle_item': 'true'
          }
        };
        console.log(`[Bundle] Fragrance ${index + 1} prepared:`, item);
        return item;
      });

      allItems.push(...fragranceItems);
    }

    console.log('[Bundle] Total items prepared:', allItems.length);
    return allItems;
  }

  async addItemsBulk(items) {
    console.log('[Bundle] Attempting to add items:', items);
    
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items: items })
    });

    console.log('[Bundle] Cart API response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('❌ [Bundle] Bulk Cart API Error:', {
        status: response.status,
        statusText: response.statusText,
        errorData: errorData,
        sentItems: items
      });
      
      // User-friendly error message
      const errorMsg = errorData.description || errorData.message || `Erreur ${response.status}: ${response.statusText}`;
      throw new Error(errorMsg);
    }

    const result = await response.json();
    console.log('✅ [Bundle] Items successfully added:', result);

    return result;
  }

  // Refresh cart state and UI after bundle addition
  async refreshCartState() {
    try {
      
      // Method 1: Use CartStateManager for state refresh
      if (window.CartStateManager) {

        await window.CartStateManager.getCart(true); // Force fresh cart data

      }
      
      // Method 2: Use your cart system's proper refresh mechanism
      if (window.CartQuantity && typeof window.CartQuantity.refreshCartDrawer === 'function') {

        const cart = window.CartStateManager ? await window.CartStateManager.getCart(false) : null;
        await window.CartQuantity.refreshCartDrawer(cart);

        
        // Give the CartQuantity system extra time to complete DOM updates
        await new Promise(resolve => setTimeout(resolve, 200));
      }
      
      // Method 3: Invalidate CartDOM cache for fresh queries
      if (window.CartDOM && typeof window.CartDOM.invalidate === 'function') {

        window.CartDOM.invalidate('bundle-add');
      }
      
      
      cartEvents.forEach(eventName => {
        document.dispatchEvent(new CustomEvent(eventName, { 
          bubbles: true,
          detail: { source: 'bundle-add' }
        }));
      });

      
    } catch (error) {
      console.error('[Bundle] Error refreshing cart state:', error);
    }
  }

  // Wait for cart drawer content to be properly updated using your cart system
  async waitForCartDrawerUpdate() {
    
    return new Promise((resolve) => {
      // Listen for cart DOM replacement event that your system dispatches
      const handleDOMReplaced = () => {

        setTimeout(() => {
          this.verifyCartContent();
          resolve();
        }, 100);
        document.removeEventListener('cart:dom-replaced', handleDOMReplaced);
      };
      
      // Set up the listener
      document.addEventListener('cart:dom-replaced', handleDOMReplaced);
      
      // Also add a timeout fallback in case the event doesn't fire
      setTimeout(() => {
        document.removeEventListener('cart:dom-replaced', handleDOMReplaced);
        this.verifyCartContent();
        resolve();
      }, 1000);
      
      // Give the CartQuantity system a moment to process
      setTimeout(() => {
        // If DOM replacement hasn't happened yet, check anyway
        if (document.querySelector('#CartDrawer-CartItems .cart-item-modern')) {
          document.removeEventListener('cart:dom-replaced', handleDOMReplaced);
          this.verifyCartContent();
          resolve();
        }
      }, 300);
    });
  }
  
  verifyCartContent() {
    // Use multiple selectors to find cart items
    const possibleSelectors = [
      '#CartDrawer-CartItems .cart-item-modern',
      '#CartDrawer-CartItems .cart-item',
      '#CartDrawer-CartItems [data-cart-item]',
      '.cart-drawer .cart-item',
      'cart-drawer-items .cart-item'
    ];
    
    let cartItems = null;
    let itemCount = 0;
    
    for (const selector of possibleSelectors) {
      if (window.CartDOM) {
        cartItems = window.CartDOM.getAll(selector);
      } else {
        cartItems = document.querySelectorAll(selector);
      }
      
      itemCount = cartItems ? cartItems.length : 0;
      if (itemCount > 0) {
        break;
      }
    }
    
    if (itemCount === 0) {
      console.warn('[Bundle] No cart items found after refresh, cart drawer may be empty');
    }
    
    return itemCount;
  }

  // Open cart drawer using your cart system methods
  async openCartDrawer() {
    
    // Method 1: Try cart drawer toggle button (most reliable)
    const cartToggle = document.querySelector('cart-drawer-items summary, .header__icon--cart, [data-cart-toggle], .cart-count-bubble');
    if (cartToggle) {
      cartToggle.click();
      return;
    }
    
    // Method 2: Try direct cart drawer manipulation
    const cartDrawer = document.querySelector('cart-drawer, #CartDrawer');
    if (cartDrawer) {
      
      // Try the open method if available
      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
        return;
      }
      
      // Fallback: manipulate attributes and classes
      cartDrawer.setAttribute('open', '');
      cartDrawer.classList.add('active', 'open', 'is-open');
      
      // Show overlay if it exists
      const overlay = document.querySelector('#CartDrawer-Overlay, .drawer-overlay');
      if (overlay) {
        overlay.style.display = 'block';
      }
      return;
    }
    
    // Method 3: Dispatch cart open events that your system listens to

    const openEvents = ['cart:open', 'cart-drawer:open', 'drawerOpen'];
    openEvents.forEach(eventName => {
      document.dispatchEvent(new CustomEvent(eventName, { 
        bubbles: true,
        detail: { source: 'bundle-add' }
      }));
    });
  }

  // Fix iOS drawer height issues after bundle addition
  fixIOSDrawerHeight() {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (!isIOS) return;

    console.log('[Bundle] Fixing iOS drawer height...');
    
    const cartDrawer = document.querySelector('cart-drawer');
    const drawerInner = document.querySelector('.drawer__inner');
    
    if (!cartDrawer || !drawerInner) {
      console.warn('[Bundle] Drawer elements not found for iOS fix');
      return;
    }

    // Force reflow and recalculation
    const currentHeight = window.innerHeight;
    const visualViewportHeight = window.visualViewport ? window.visualViewport.height : currentHeight;
    
    console.log('[Bundle] iOS heights - inner:', currentHeight, 'visual:', visualViewportHeight);
    
    // Force drawer to recalculate its height
    drawerInner.style.height = `${visualViewportHeight}px`;
    
    // Trigger a reflow
    void drawerInner.offsetHeight;
    
    // Reset to dvh (will use CSS value)
    setTimeout(() => {
      drawerInner.style.height = '';
      console.log('[Bundle] iOS drawer height fixed');
    }, 50);
  }

  async addMainProductToCart() {
    // This method is now deprecated - use prepareAllBundleItems + addItemsBulk instead
    const variantInput = this.productForm.querySelector('[name="id"]');
    if (!variantInput) {
      throw new Error('Variant ID not found');
    }

    const variantId = variantInput.value;
    const quantity = this.productForm.querySelector('[name="quantity"]')?.value || 1;

    const mainProductData = {
      id: variantId,
      quantity: parseInt(quantity),
      properties: {
        'Bundle': 'Coffret Sur Mesure 3x15ml'
      }
    };

    await this.addItemToCartDirect(mainProductData);
  }

  // ============================
  // BUNDLE ADDITION WITH PROPER PRICING
  // ============================
  
  async addSelectedProductsToCart() {
    // This method is now deprecated - use addSelectedProductsToCartBatched instead
    const selectedProducts = this.selectedProducts.filter(p => p !== null);
    if (selectedProducts.length === 0) {
      alert('Veuillez sélectionner au moins un produit pour votre bundle.');
      return;
    }

    try {
      await this.addSelectedProductsToCartBatched(selectedProducts);
      this.closeModal();
      this.clearSelectedProducts();

    } catch (error) {
      console.error('Error adding bundle items to cart:', error);
      alert('Erreur lors de l\'ajout au panier. Veuillez réessayer.');
    }
  }

  async addSelectedProductsToCartBatched(selectedProducts) {
    // First, verify all variant IDs by fetching fresh product data
    const verifiedProducts = await Promise.all(
      selectedProducts.map(product => this.verifyAndGetValidVariant(product))
    );

    // Prepare bundle items with verified variant IDs
    const bundleItems = verifiedProducts.map(product => {
      
      return {
        id: product.validVariantId,
        quantity: 1,
        properties: { 
          'Part of': 'Coffret Sur Mesure 3x15ml',
          'Coffret-Bundle': 'true',
          '_bundle_item': 'true'
        },
        title: product.title
      };
    });


    // Add items sequentially with delay to avoid rate limiting
    for (const item of bundleItems) {
      try {
        await this.addItemToCartDirect(item);

        // Small delay between requests to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 200));
      } catch (error) {
        console.error(`Failed to add ${item.title} to cart:`, error);
        throw error; // Re-throw to stop the process
      }
    }
  }

  async verifyAndGetValidVariant(product) {
    try {
      // Fetch fresh product data from Shopify
      const response = await fetch(`/products/${product.handle}.js`);
      if (!response.ok) {
        throw new Error(`Failed to fetch product data for ${product.handle}`);
      }
      
      const freshProductData = await response.json();

      
      // Find the first available variant
      const availableVariant = freshProductData.variants.find(v => v.available);
      const firstVariant = freshProductData.variants[0];
      
      if (!availableVariant && !firstVariant) {
        throw new Error(`No variants found for product ${product.title}`);
      }
      
      const selectedVariant = availableVariant || firstVariant;

      
      return {
        ...product,
        validVariantId: selectedVariant.id,
        freshData: freshProductData
      };
      
    } catch (error) {
      console.error(`Error verifying product ${product.title}:`, error);
      
      // Fallback to original data
      let fallbackVariantId;
      if (product.variant_id) {
        fallbackVariantId = product.variant_id;
      } else if (product.variants && product.variants.length > 0) {
        const availableVariant = product.variants.find(v => v.available) || product.variants[0];
        fallbackVariantId = availableVariant.id;
      } else {
        fallbackVariantId = product.id;
      }
      
      console.warn(`Using fallback variant ID ${fallbackVariantId} for ${product.title}`);
      
      return {
        ...product,
        validVariantId: fallbackVariantId
      };
    }
  }
  
  async addItemToCartDirect(item) {

    
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(item)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Cart API Error Response:', {
        status: response.status,
        statusText: response.statusText,
        errorData: errorData,
        sentItem: item
      });
      throw new Error(errorData.description || `Failed to add item to cart: ${response.statusText}`);
    }

    const result = await response.json();

    return result;
  }

  async addItemsToCartDirect(items) {
    // Deprecated - use addItemToCartDirect with sequential calls instead
    for (const item of items) {
      await this.addItemToCartDirect(item);
      await new Promise(resolve => setTimeout(resolve, 200)); // Rate limiting
    }
  }

  // ============================
  // EXISTING MODAL FUNCTIONALITY
  // ============================

  async loadBundleProducts() {
    try {
      const dataScript = document.querySelector(`#bundle-products-data-${this.blockId}`);
      if (!dataScript) {
        console.error('Bundle products data script not found.');
        return;
      }

      this.bundleProductsData = JSON.parse(dataScript.textContent);

    } catch (error) {
      console.error('Error loading bundle products data:', error);
    }
  }

  setupModal() {
    // Add button click handlers
    this.bundleContainer.querySelectorAll('.product-bundle__add').forEach((button, index) => {
      button.addEventListener('click', () => this.openModal(index));
    });

    // Modal controls
    this.modal.querySelector('.bundle-modal-close')?.addEventListener('click', () => this.closeModal());
    this.modal.querySelector('.bundle-modal-back')?.addEventListener('click', () => this.closeModal());
    this.modal.querySelector('.bundle-overlay')?.addEventListener('click', () => this.closeModal());

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('active')) {
        this.closeModal();
      }
    });
  }

  openModal(slotIndex) {
    this.currentSlot = slotIndex;
    this.renderProducts();
    this.modal.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  closeModal() {
    this.modal.classList.remove('active');
    document.body.style.overflow = '';
    this.currentSlot = null;
  }

  renderProducts() {
    const container = this.modal.querySelector('.bundle-options-container');
    
    if (!this.bundleProductsData || this.bundleProductsData.length === 0) {
      container.innerHTML = '<div class="bundle-empty">Aucun produit disponible pour le bundle.</div>';
      return;
    }

    const productsGrid = document.createElement('div');
    productsGrid.className = 'bundle-products-grid';

    const sortedProducts = [...this.bundleProductsData].sort((a, b) => a.title.localeCompare(b.title, 'fr'));

    sortedProducts.forEach(product => {
      const productCard = this.createProductCard(product);
      productsGrid.appendChild(productCard);
    });

    container.innerHTML = '';
    container.appendChild(productsGrid);
  }

  createProductCard(product) {
    const card = document.createElement('div');
    card.className = 'bundle-product-card';
    
    // Vérifier si le produit est sélectionné dans n'importe quel slot
    const isSelectedInCurrentSlot = this.selectedProducts[this.currentSlot]?.id === product.id;
    const isSelectedInAnySlot = this.selectedProducts.some(selectedProduct => selectedProduct?.id === product.id);
    
    card.innerHTML = `
      <div class="bundle-product-card__inner">
        <div class="bundle-product-card__image">
          <img src="${product.image}" alt="${product.title}" loading="lazy">
          ${isSelectedInAnySlot ? `<span class="bundle-product-card__selected-badge">${Shopify.locale === 'fr' ? 'Sélectionné' : 'Selected'}</span>` : ''}
        </div>
        <div class="bundle-product-card__content-text">
          <h3 class="bundle-product-card__title">${product.title}</h3>
          <p class="bundle-product-card__description">${product.subdescription || product.description}</p>
        </div>
        <button class="bundle-product-card__button ${isSelectedInCurrentSlot ? 'bundle-product-card__button--selected' : ''}" 
                data-product-id="${product.id}">
          ${isSelectedInCurrentSlot ? (Shopify.locale === 'fr' ? 'Sélectionné' : 'Selected') : (Shopify.locale === 'fr' ? 'Sélectionner' : 'Select')}
        </button>
      </div>
    `;

    card.querySelector('.bundle-product-card__button').addEventListener('click', () => {
      this.selectProduct(product);
    });

    return card;
  }

  selectProduct(product) {
    this.selectedProducts[this.currentSlot] = product;
    this.updateBundleDisplay();
    this.closeModal();
  }

  updateBundleDisplay() {
    this.bundleContainer.querySelectorAll('.product-bundle__item').forEach((item, index) => {
      const product = this.selectedProducts[index];
      const addButton = item.querySelector('.product-bundle__add');
      const wrapper = item.querySelector('.product-bundle__add-wrapper');

      if (product) {
        wrapper.innerHTML = `
          <div class="product-bundle__item-content">
            <div class="product-bundle__image">
              <img src="${product.image}" alt="${product.title}">
            </div>
            <div class="product-bundle__details">
              <h4 class="product-bundle__title">${product.title}</h4>
              <div class="product-bundle__price">0€</div>
            </div>
            <button type="button" class="product-bundle__edit" data-index="${index}">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M2 11.5V14h2.5L12.5 6L10 3.5L2 11.5z" stroke="currentColor" stroke-width="1.5"/>
              </svg>
            </button>
          </div>
        `;

        // Add edit functionality
        wrapper.querySelector('.product-bundle__edit').addEventListener('click', () => {
          this.openModal(index);
        });
      }
    });

    this.updateBundleTotal();
    this.showAddToCartButton();
  }

  updateBundleTotal() {
    const selectedCount = this.selectedProducts.filter(p => p !== null).length;
    const totalContainer = this.bundleContainer.querySelector('.product-bundle__total');
    
    if (selectedCount > 0) {
      totalContainer.style.display = 'none';
      totalContainer.querySelector('.product-bundle__total-price').textContent = '0€';
    } else {
      totalContainer.style.display = 'none';
    }
  }

  showAddToCartButton() {
    const selectedCount = this.selectedProducts.filter(p => p !== null).length;
    const bundleComplete = selectedCount === 3;

    // Update the original add to cart button text + enabled state to reflect bundle status
    if (this.originalAddToCartButton) {
      if (bundleComplete) {
        this.originalAddToCartButton.textContent = Shopify.locale === 'fr' ? 'Ajouter le coffret au panier' : 'Add the set to cart';
      } else {
        // Show how many more fragrances are needed
        const remaining = 3 - selectedCount;
        this.originalAddToCartButton.textContent = Shopify.locale === 'fr'
          ? `Sélectionnez ${remaining} parfum${remaining > 1 ? 's' : ''} de plus`
          : `Select ${remaining} more fragrance${remaining > 1 ? 's' : ''}`;
      }

      // Fail-safe gating: the button ships disabled in HTML (data-bundle-gated) and is only
      // enabled here once the 3 fragrances are selected. If this script never runs, the
      // button stays disabled — the coffret can't be added to cart incomplete.
      this.originalAddToCartButton.disabled = !bundleComplete;
      if (bundleComplete) {
        this.originalAddToCartButton.removeAttribute('aria-disabled');
      } else {
        this.originalAddToCartButton.setAttribute('aria-disabled', 'true');
      }
    }

    // Remove any existing custom bundle buttons
    const existingButton = this.bundleContainer.querySelector('.bundle-add-to-cart');
    if (existingButton) {
      existingButton.remove();
    }
  }

  clearSelectedProducts() {
    this.selectedProducts = [null, null, null];
    this.updateBundleDisplay();
  }

  // ============================
  // CLEANUP METHOD
  // ============================
  
  cleanup() {
    // Unsubscribe from cart state manager
    if (this.cartSubscription) {
      this.cartSubscription();
      this.cartSubscription = null;
    }
    
    // Remove event listeners
    document.removeEventListener('cart:updated', this.handleCartEvent);
    document.removeEventListener('cart:refresh', this.handleCartEvent);

  }
}

// ============================
// GLOBAL INITIALIZATION
// ============================

function initBundleManager() {
  // Wait for cart system to be ready
  const waitForCartSystem = () => {
    if (window.CartStateManager) {
      const bundleManager = new BundleManager();
      window.bundleManager = bundleManager;
      
      // Global cleanup function for debugging
      window.cleanupOrphanedBundleItems = function() {
        if (window.bundleManager) {
          return window.bundleManager.checkForBundleCleanupFromEvent();
        }
        console.warn('Bundle manager not available');
      };
      
    } else {

      setTimeout(waitForCartSystem, 100);
    }
  };
  
  waitForCartSystem();
}

// Check if cart drawer should open or notification should show after page refresh
function checkOpenCartAfterRefresh() {
  // Check if we should show notification instead (iOS only)
  if (sessionStorage.getItem('showBundleNotificationAfterRefresh') === 'true') {
    sessionStorage.removeItem('showBundleNotificationAfterRefresh');
    
    // Wait for page to fully load, then show notification
    setTimeout(() => {
      console.log('[Bundle] Showing success notification after refresh');
      showBundleSuccessNotification();
    }, 500);
    return;
  }
  
  // Normal behavior: open cart drawer
  if (sessionStorage.getItem('openCartAfterRefresh') === 'true') {
    
    // Clear the flag
    sessionStorage.removeItem('openCartAfterRefresh');
    
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const initialDelay = isIOS ? 800 : 500; // Longer delay for iOS
    
    // Wait for page to fully load, then open cart drawer
    setTimeout(() => {
      // Wait for CartDOM to be ready on iOS
      if (isIOS && window.CartDOM) {
        window.CartDOM.invalidate('page-reload');
      }
      
      // Try multiple methods to open cart drawer
      const cartToggle = document.querySelector('cart-drawer-items summary, .header__icon--cart, [data-cart-toggle], .cart-count-bubble');
      if (cartToggle) {
        cartToggle.click();
        
        // iOS: Fix drawer height after opening with longer delay
        setTimeout(() => {
          fixIOSDrawerHeightGlobal();
          forceCartDrawerScrollTop();
          forceCartItemsLayoutRecalc();
          stabilizeCartDrawerIOS();
          
          // Additional height fix after a moment
          if (isIOS) {
            setTimeout(() => {
              fixIOSDrawerHeightGlobal();
              forceCartDrawerScrollTop();
              forceCartItemsLayoutRecalc();
            }, 500);
          }
        }, isIOS ? 600 : 300);
        return;
      }
      
      // Fallback: try direct cart drawer manipulation
      const cartDrawer = document.querySelector('cart-drawer, #CartDrawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
        
        // iOS: Fix drawer height after opening with longer delay
        setTimeout(() => {
          fixIOSDrawerHeightGlobal();
          forceCartDrawerScrollTop();
          forceCartItemsLayoutRecalc();
          stabilizeCartDrawerIOS();
          
          // Additional height fix after a moment
          if (isIOS) {
            setTimeout(() => {
              fixIOSDrawerHeightGlobal();
              forceCartDrawerScrollTop();
              forceCartItemsLayoutRecalc();
            }, 500);
          }
        }, isIOS ? 600 : 300);
        return;
      }
      
      // Fallback: dispatch events
      document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
      
      // iOS: Fix drawer height after opening with longer delay
      setTimeout(() => {
        fixIOSDrawerHeightGlobal();
        forceCartDrawerScrollTop();
        forceCartItemsLayoutRecalc();
        stabilizeCartDrawerIOS();
        
        // Additional height fix after a moment
        if (isIOS) {
          setTimeout(() => {
            fixIOSDrawerHeightGlobal();
            forceCartDrawerScrollTop();
            forceCartItemsLayoutRecalc();
          }, 500);
        }
      }, isIOS ? 600 : 300);
      
    }, initialDelay);
  }
}

// Force scroll to top of cart drawer (helps with iOS height calculation)
function forceCartDrawerScrollTop() {
  const drawerInner = document.querySelector('.drawer__inner');
  const cartItems = document.querySelector('#CartDrawer-CartItems');
  const cartDrawerItems = document.querySelector('cart-drawer-items');
  
  if (drawerInner) {
    drawerInner.scrollTop = 0;
  }
  if (cartItems) {
    cartItems.scrollTop = 0;
  }
  if (cartDrawerItems) {
    cartDrawerItems.scrollTop = 0;
  }
}

// Force recalculation of cart drawer items container layout on iOS
function forceCartItemsLayoutRecalc() {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (!isIOS) return;
  
  console.log('[Bundle] Forcing cart items layout recalculation...');
  
  const cartDrawerItems = document.querySelector('cart-drawer-items');
  const drawerContents = document.querySelector('.drawer__contents');
  const drawerFooter = document.querySelector('.drawer__footer, .cart-footer');
  
  if (!cartDrawerItems) {
    console.warn('[Bundle] cart-drawer-items not found for layout fix');
    return;
  }
  
  // Force flexbox recalculation by temporarily removing and restoring flex
  const originalFlex = cartDrawerItems.style.flex;
  cartDrawerItems.style.flex = 'none';
  
  // Force reflow
  void cartDrawerItems.offsetHeight;
  
  // Restore flex
  cartDrawerItems.style.flex = originalFlex || '';
  
  // Also force reflow on parent and footer
  if (drawerContents) {
    void drawerContents.offsetHeight;
  }
  if (drawerFooter) {
    void drawerFooter.offsetHeight;
  }
  
  // Trigger one more reflow
  void cartDrawerItems.offsetHeight;
  
  console.log('[Bundle] Cart items layout recalculation complete');
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initBundleManager();
    checkOpenCartAfterRefresh();
  });
} else {
  initBundleManager();
  checkOpenCartAfterRefresh();
}

// ============================
// BUNDLE LOADING OVERLAY
// ============================

function showBundleLoadingOverlay() {
  // Check if overlay already exists
  let overlay = document.getElementById('bundle-loading-overlay');
  if (overlay) {
    overlay.style.display = 'flex';
    return;
  }

  // Create overlay
  overlay = document.createElement('div');
  overlay.id = 'bundle-loading-overlay';
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 99999;
    backdrop-filter: blur(4px);
  `;

  // Create content container
  const content = document.createElement('div');
  content.style.cssText = `
    background: white;
    border-radius: 12px;
    padding: 40px;
    text-align: center;
    max-width: 400px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  `;

  // Create spinner
  const spinner = document.createElement('div');
  spinner.style.cssText = `
    width: 50px;
    height: 50px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #1f1d1a;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 20px;
  `;

  // Create text
  const text = document.createElement('p');
  text.style.cssText = `
    font-size: 18px;
    font-weight: 500;
    color: #1f1d1a;
    margin: 0;
    line-height: 1.5;
  `;
  const isEnglish = Shopify.locale === 'en';
  text.textContent = isEnglish ? 'Composing your set...' : 'Composition du coffret en cours...';

  // Add spinner animation
  const style = document.createElement('style');
  style.textContent = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);

  // Assemble
  content.appendChild(spinner);
  content.appendChild(text);
  overlay.appendChild(content);
  document.body.appendChild(overlay);
}

function hideBundleLoadingOverlay() {
  const overlay = document.getElementById('bundle-loading-overlay');
  if (overlay) {
    overlay.style.display = 'none';
    // Don't remove, just hide for potential reuse
  }
}

// ============================
// BUNDLE SUCCESS NOTIFICATION
// ============================

function showBundleSuccessNotification() {
  const isEnglish = typeof Shopify !== 'undefined' && Shopify.locale === 'en';
  
  // Check if notification already exists
  let existingNotif = document.getElementById('bundle-success-notification');
  if (existingNotif) {
    existingNotif.remove();
  }
  
  // Create notification
  const notification = document.createElement('div');
  notification.id = 'bundle-success-notification';
  notification.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    background: #1f1d1a;
    color: white;
    padding: 12px 16px;
    border-radius: 6px;
    z-index: 10000;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    display: flex;
    align-items: center;
    gap: 10px;
    font-family: "Work Sans", sans-serif;
    animation: slideInRight 0.3s ease-out;
    max-width: 320px;
  `;
  
  // Add animation
  if (!document.getElementById('bundle-notification-animation')) {
    const style = document.createElement('style');
    style.id = 'bundle-notification-animation';
    style.textContent = `
      @keyframes slideInRight {
        from {
          transform: translateX(400px);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
      @keyframes slideOutRight {
        from {
          transform: translateX(0);
          opacity: 1;
        }
        to {
          transform: translateX(400px);
          opacity: 0;
        }
      }
    `;
    document.head.appendChild(style);
  }
  
  // Create content
  const checkmark = document.createElement('span');
  checkmark.textContent = '✓';
  checkmark.style.cssText = `
    font-size: 18px;
    font-weight: bold;
  `;
  
  const message = document.createElement('span');
  message.textContent = isEnglish ? 'Set added to cart' : 'Coffret ajouté au panier';
  message.style.cssText = `
    flex: 1;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.4;
  `;
  
  const button = document.createElement('button');
  button.textContent = isEnglish ? 'View' : 'Voir';
  button.style.cssText = `
    background: white;
    color: #1f1d1a;
    border: none;
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    font-family: "Work Sans", sans-serif;
    transition: all 0.2s ease;
    white-space: nowrap;
  `;
  
  button.onmouseover = () => {
    button.style.background = '#f5f5f5';
  };
  button.onmouseout = () => {
    button.style.background = 'white';
  };
  
  button.onclick = () => {
    const cartToggle = document.querySelector('.header__icon--cart, [data-cart-toggle], cart-drawer-items summary');
    if (cartToggle) {
      cartToggle.click();
    }
    removeNotification();
  };
  
  // Assemble
  notification.appendChild(checkmark);
  notification.appendChild(message);
  notification.appendChild(button);
  document.body.appendChild(notification);
  
  // Auto-hide after 5 seconds
  setTimeout(() => {
    removeNotification();
  }, 5000);
  
  function removeNotification() {
    if (notification && notification.parentElement) {
      notification.style.animation = 'slideOutRight 0.3s ease-out';
      setTimeout(() => {
        if (notification.parentElement) {
          notification.remove();
        }
      }, 300);
    }
  }
}

// ============================
// iOS CART DRAWER STABILIZATION
// ============================

function isIOSDevice() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

// Global function to fix iOS drawer height (callable from checkOpenCartAfterRefresh)
function fixIOSDrawerHeightGlobal() {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (!isIOS) return;

  console.log('[Bundle Global] Fixing iOS drawer height...');
  
  const cartDrawer = document.querySelector('cart-drawer');
  const drawerInner = document.querySelector('.drawer__inner');
  const cartItems = document.querySelector('#CartDrawer-CartItems');
  
  if (!cartDrawer || !drawerInner) {
    console.warn('[Bundle Global] Drawer elements not found for iOS fix');
    return;
  }

  // Force reflow and recalculation
  const currentHeight = window.innerHeight;
  const visualViewportHeight = window.visualViewport ? window.visualViewport.height : currentHeight;
  
  console.log('[Bundle Global] iOS heights - inner:', currentHeight, 'visual:', visualViewportHeight);
  
  // Remove any inline height first to reset
  drawerInner.style.height = '';
  
  // Force reflow
  void drawerInner.offsetHeight;
  
  // Set explicit height
  drawerInner.style.height = `${visualViewportHeight}px`;
  
  // Also fix cart items container if it exists
  if (cartItems) {
    cartItems.style.minHeight = 'auto';
    void cartItems.offsetHeight;
  }
  
  // Trigger another reflow
  void drawerInner.offsetHeight;
  
  // Reset to dvh (will use CSS value) after a moment
  setTimeout(() => {
    drawerInner.style.height = '';
    console.log('[Bundle Global] iOS drawer height fixed');
    
    // Force one final reflow
    void drawerInner.offsetHeight;
  }, 100);
}

function stabilizeCartDrawerIOS() {
  if (!isIOSDevice()) return;
  try {
    // Single adjustment - simplified approach
    window.CartSafariViewport?.forceAdjustment?.();
  } catch (e) {}
}
